import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { v4 as uuidv4 } from 'uuid';

import { AppState } from 'src/app/state';
import { saveIntervention, updateIntervention } from 'src/app/dashboard/state/intervention/intervention.actions';
import { uploadReset } from 'src/app/dashboard/state/upload/upload.actions';
import { selectSelectedLanguage, selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import { selectSelectedIntervention } from 'src/app/dashboard/state/intervention/intervention.selectors';
import { selectModules } from '../../../../state/module/module.selectors';
import { selectInformativePages } from 'src/app/dashboard/state/informative-page/informative-page.selectors';
import { environment } from 'src/environments/environment';
import { cannotContainSpace, customMinLength, removeWhitespaces } from 'src/app/core/custom-form-validators';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';
import { calculateTimerOffset, setTimerValues, TIMER_ARRAY_UNITS } from '../../../helpers/time-array-helper';
import { Module } from '../../../../../models/module';
import { Study } from 'src/app/models/study';
import { Intervention } from 'src/app/models/intervention';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { InformativePage } from 'src/app/models/informative-page';
import { Language } from 'src/app/models/language';
import { StudyType } from '../../../../../models/study-type';

@Component({
  selector: 'dashboard-intervention-form',
  templateUrl: './intervention-form.component.html',
  styleUrls: ['./intervention-form.component.scss'],
})
export class InterventionFormComponent implements OnInit, OnDestroy {
  @Input() isEdit: boolean;

  imgPath: string;
  isImageToEdit: boolean;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_INTERVENTION;

  interventionForm: FormGroup;
  StudyType = StudyType;
  careerUrl = environment.careerUrl;

  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  selectedIntervention$: Observable<Intervention>;
  selectedIntervention: Intervention;

  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;

  informativePages$: Observable<InformativePage[]>;
  modules$: Observable<Module[]>;

  imageUploadUuid: string;
  TIMER_ARRAY_UNITS = TIMER_ARRAY_UNITS;
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    public privilegeService: PrivilegeService
  ) {}

  get name(): AbstractControl {
    return this.interventionForm.get('name') as AbstractControl;
  }

  get imagePath(): AbstractControl {
    return this.interventionForm.get('imagePath') as AbstractControl;
  }

  get description(): AbstractControl {
    return this.interventionForm.get('description') as AbstractControl;
  }

  get hash(): AbstractControl {
    return this.interventionForm.get('hash') as AbstractControl;
  }

  get informationPageId(): AbstractControl {
    return this.interventionForm.get('informationPageId') as AbstractControl;
  }

  get startFromPhone(): AbstractControl {
    return this.interventionForm.get('startFromPhone') as AbstractControl;
  }

  get sendReportAfterArray(): FormArray {
    return this.interventionForm.get('sendReportAfterArray') as FormArray;
  }

  get isDefault(): AbstractControl {
    return this.interventionForm.get('isDefault') as AbstractControl;
  }

  ngOnInit(): void {
    this.interventionForm = this.formBuilder.group({
      name: ['', [Validators.required, removeWhitespaces, customMinLength(3)]],
      description: ['', [Validators.required, removeWhitespaces]],
      imagePath: [null],
      hash: [null, [Validators.maxLength(20), cannotContainSpace]],
      informationPageId: [null],
      moduleId: [null],
      startFromPhone: [{ value: false, disabled: this.isEdit }],
      sendReportAfterArray: this.formBuilder.array([
        this.formBuilder.control(0),
        this.formBuilder.control(0),
        this.formBuilder.control(0),
        this.formBuilder.control(0),
      ]),
      isDefault: [0],
    });

    this.imageUploadUuid = uuidv4();

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.subscriptions.add(this.selectedStudy$.subscribe((selectedStudy) => (this.selectedStudy = selectedStudy)));

    this.informativePages$ = this.store.select(selectInformativePages);
    this.modules$ = this.store.select(selectModules);

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions.add(this.selectedLanguage$.subscribe((language) => (this.selectedLanguage = language)));

    this.idsForUpload = {
      studyId: this.selectedStudy?.id,
    };

    this.selectedIntervention$ = this.store.select(selectSelectedIntervention);
    this.subscriptions.add(
      this.selectedIntervention$.subscribe((intervention) => {
        this.selectedIntervention = intervention;

        this.checkPrivileges();

        if (!intervention) {
          this.interventionForm.reset();
        } else {
          this.isEdit = true;
        }

        if (intervention?.imagePath) {
          this.isImageToEdit = true;
          this.imgPath = intervention.imagePath;
        } else {
          this.isImageToEdit = false;
          this.imgPath = null;
          this.store.dispatch(uploadReset({ uuid: this.imageUploadUuid }));
        }

        this.idsForUpload = {
          studyId: intervention?.studyId,
          interventionId: intervention?.id,
        };

        if (this.isEdit) {
          setTimerValues(this.selectedIntervention?.sendReportAfter, this.sendReportAfterArray);
        }
      })
    );

    if (this.isEdit) {
      this.interventionForm
        .get('hash')
        .setValidators([Validators.required, Validators.maxLength(20), cannotContainSpace]);
      this.interventionForm.updateValueAndValidity();
    }

    this.setSendReportAfterInputEvent();
  }

  checkPrivileges(): void {
    if (
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.INTERVENTIONS, PRIVILEGE_ACTIONS.VIEW) &&
      !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.INTERVENTIONS, PRIVILEGE_ACTIONS.ALL)
    ) {
      this.interventionForm.disable();
    }
  }

  setSendReportAfterInputEvent() {
    this.subscriptions.add(
      this.sendReportAfterArray.valueChanges
        .pipe(
          map(() => calculateTimerOffset(this.sendReportAfterArray)),
          debounceTime(100),
          distinctUntilChanged(),
          tap((sendReportAfterOffset) => {
            setTimerValues(sendReportAfterOffset, this.sendReportAfterArray);
          })
        )
        .subscribe()
    );
  }

  generateRandomHash(length = 5): string {
    return Math.random()
      .toString(36)
      .substring(2, 2 + length)
      .toUpperCase();
  }

  saveIntervention(): void {
    let interventionDetails;

    if (this.interventionForm.invalid) {
      this.interventionForm.markAllAsTouched();
      return;
    }

    interventionDetails = {
      ...this.interventionForm.value,
      studyId: this.selectedStudy.id,
    };

    if (this.selectedStudy?.type === StudyType.TIIM) {
      delete interventionDetails.startFromPhone;
      delete interventionDetails.sendReportAfter;

      interventionDetails = {
        ...interventionDetails,
        isDefault: +interventionDetails.isDefault,
      };
    }

    if (this.selectedStudy?.type === StudyType.CC) {
      interventionDetails.sendReportAfter = calculateTimerOffset(this.sendReportAfterArray);
      delete interventionDetails.startFromPhone;
    }

    delete interventionDetails.sendReportAfterArray;

    if (this.isEdit) {
      interventionDetails = {
        ...interventionDetails,
        id: this.selectedIntervention.id,
      };

      this.store.dispatch(updateIntervention({ intervention: interventionDetails }));
      return;
    }

    if (this.selectedStudy?.type !== StudyType.CC) {
      delete interventionDetails.hash;
    } else {
      if (!interventionDetails.hash) {
        interventionDetails = {
          ...interventionDetails,
          hash: this.generateRandomHash(),
        };
      }
    }

    this.store.dispatch(saveIntervention({ intervention: interventionDetails }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
